import { Logo } from "@/components/Logo";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { UpdateBanner } from "@/components/UpdateBanner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import LoginPage from "@/pages/LoginPage";
import { DemoTour } from "@/components/DemoTour";
import JoinPage from "@/pages/JoinPage";
import InvitePage from "@/pages/InvitePage";

import DriverLayout from "@/pages/driver/DriverLayout";
import DriverHome from "@/pages/driver/DriverHome";
import ToRecover from "@/pages/driver/ToRecover";
import EstablishmentDetail from "@/pages/driver/EstablishmentDetail";
import Feedback from "@/pages/driver/Feedback";
import MyPickups from "@/pages/driver/MyPickups";
import ReportMissing from "@/pages/driver/ReportMissing";
import DriverHistory from "@/pages/driver/DriverHistory";
import MyPoints from "@/pages/driver/MyPoints";

import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import RequestsList from "@/pages/admin/RequestsList";
import Patients from "@/pages/admin/Patients";
import Establishments from "@/pages/admin/Establishments";
import Users from "@/pages/admin/Users";
import Settings from "@/pages/admin/Settings";
import Appointments from "@/pages/admin/Appointments";
import NotificationLog from "@/pages/admin/NotificationLog";
import AddAppointment from "@/pages/driver/AddAppointment";
import ReportVehicle from "@/pages/driver/ReportVehicle";
import VehicleReports from "@/pages/admin/VehicleReports";
import NewClient from "@/pages/driver/NewClient";
import NewClients from "@/pages/admin/NewClients";
import Leaves from "@/pages/driver/Leaves";
import LeavesAdmin from "@/pages/admin/LeavesAdmin";
import HoursAdmin from "@/pages/admin/HoursAdmin";
import PlanningSettings from "@/pages/admin/PlanningSettings";
import PlanningEntry from "@/pages/admin/PlanningEntry";
import PlanningLife from "@/pages/admin/PlanningLife";
import MyPlanning from "@/pages/driver/MyPlanning";
import MyHours from "@/pages/driver/MyHours";
import StaffLayout from "@/pages/staff/StaffLayout";
import StaffHome from "@/pages/staff/StaffHome";
import GuardPreferences from "@/pages/driver/GuardPreferences";
import PatientApp from "@/pages/patient/PatientApp";
import VehicleEquipmentDriver from "@/pages/driver/VehicleEquipmentDriver";
import { getSpace } from "@/lib/space";
import PatientAccess from "@/pages/admin/PatientAccess";
import PointsMonthly from "@/pages/admin/PointsMonthly";

import SuperAdmin from "@/pages/super/SuperAdmin";
import SuperLayout from "@/pages/super/SuperLayout";
import Billing from "@/pages/super/Billing";
import BillingSettings from "@/pages/super/BillingSettings";
import Subscription from "@/pages/admin/Subscription";

const Loader = () => (
  <div data-testid="app-loader" className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
    <Logo size={44} tagline />
    <div className="w-8 h-8 rounded-full border-2 border-accent-200 border-t-accent-600 animate-spin" />
    <p className="text-sm text-slate-400">Vérification de votre session…</p>
  </div>
);

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "chauffeur") return <Navigate to="/driver" replace />;
  if (user.role === "administratif") return <Navigate to="/staff" replace />;
  if (user.role === "superadmin") return <Navigate to="/super" replace />;
  return <Navigate to="/app" replace />;
}

// Racine "/" (et start_url PWA) : dernier espace mémorisé, sinon page de choix. Ne touche jamais /patient/*.
function RootEntry() {
  const { user } = useAuth();
  const space = getSpace();
  if (space === "patient") return <Navigate to="/patient" replace />;
  if (space === null) return <LoginPage space={null} />;
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "chauffeur") return <Navigate to="/driver" replace />;
  if (user.role === "administratif") return <Navigate to="/staff" replace />;
  if (user.role === "superadmin") return <Navigate to="/super" replace />;
  return <Navigate to="/app" replace />;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <HomeRedirect />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootEntry />} />
            <Route path="/choisir" element={<LoginPage space={null} />} />
            <Route path="/login" element={<LoginPage space="entreprise" />} />
            <Route path="/patient/connexion" element={<LoginPage space="patient" />} />
            <Route path="/acces-reserve" element={<LoginPage reserved />} />
            <Route path="/rejoindre" element={<JoinPage />} />
            <Route path="/invitation" element={<InvitePage />} />
            <Route path="/patient/*" element={<PatientApp />} />

            <Route path="/driver" element={<Protected roles={["chauffeur"]}><DriverLayout /></Protected>}>
              <Route index element={<DriverHome />} />
              <Route path="a-recuperer" element={<ToRecover />} />
              <Route path="etablissement/:eid" element={<EstablishmentDetail />} />
              <Route path="retour/:rid" element={<Feedback />} />
              <Route path="rendez-vous" element={<AddAppointment />} />
              <Route path="vehicule" element={<ReportVehicle />} />
              <Route path="equipements" element={<VehicleEquipmentDriver />} />
              <Route path="nouveau-client" element={<NewClient />} />
              <Route path="conges" element={<Leaves />} />
              <Route path="heures" element={<MyHours />} />
              <Route path="planning" element={<MyPlanning />} />
              <Route path="preferences-garde" element={<GuardPreferences />} />
              <Route path="mes-recuperations" element={<MyPickups />} />
              <Route path="mes-points" element={<MyPoints />} />
              <Route path="signaler" element={<ReportMissing />} />
              <Route path="historique" element={<DriverHistory />} />
            </Route>

            <Route path="/staff" element={<Protected roles={["administratif"]}><StaffLayout /></Protected>}>
              <Route index element={<StaffHome />} />
              <Route path="heures" element={<MyHours />} />
              <Route path="conges" element={<Leaves />} />
            </Route>

            <Route path="/app" element={<Protected roles={["regulation", "admin"]}><AdminLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="demandes" element={<RequestsList />} />
              <Route path="patients" element={<Patients />} />
              <Route path="etablissements" element={<Establishments />} />
              <Route path="points" element={<PointsMonthly />} />
              <Route path="utilisateurs" element={<Users />} />
              <Route path="parametres" element={<Settings />} />
              <Route path="rendez-vous" element={<Appointments />} />
              <Route path="vehicules" element={<VehicleReports />} />
              <Route path="nouveaux-clients" element={<NewClients />} />
              <Route path="conges" element={<LeavesAdmin />} />
              <Route path="heures" element={<HoursAdmin />} />
              <Route path="planification" element={<PlanningEntry />} />
              <Route path="planification/avance" element={<PlanningSettings />} />
              <Route path="planification/vie" element={<PlanningLife />} />
              <Route path="acces-patients" element={<PatientAccess />} />
              <Route path="invitations-patient" element={<PatientAccess />} />
              <Route path="journal-notifications" element={<NotificationLog />} />
              <Route path="abonnement" element={<Subscription />} />
            </Route>

            <Route path="/super" element={<Protected roles={["superadmin"]}><SuperLayout /></Protected>}>
              <Route index element={<SuperAdmin />} />
              <Route path="facturation" element={<Billing />} />
              <Route path="parametres-facturation" element={<BillingSettings />} />
              <Route path="journal-notifications" element={<NotificationLog />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors offset={72} />
        <UpdateBanner />
        <DemoTour />
      </AuthProvider>
    </div>
  );
}

export default App;
